import { BILLING_PRODUCT } from "../services/billing/billing.const"
import { getAddonFreeQuota, getAddonStripe, getAddonTier, isAddonFreeTier } from "./addon-plan"
import { isOrgEnterprise } from "./org-plan"

/**
 * AI Expenses entitlement, read off the org document. The front door for the Expenses
 * add-on the same way purchasing-plan.js is for Purchasing - components should import
 * from here rather than reaching into orgData.expenses or addon-plan.js directly.
 *
 * Display only. The API is what actually refuses a scan: the receipts endpoint checks the
 * same licence floor and the month's quota, and answers `expense_ai/monthly_limit_reached`
 * once the month is spent.
 */

export const getExpensesStripe = (orgData) => getAddonStripe(orgData, BILLING_PRODUCT.EXPENSES)

/**
 * The AI Expenses tier an org is effectively on, resolved the same way AiExpensesPricing
 * decides whether the add-on is "live": a live Stripe subscription (active/trialing/
 * past_due) with a known tier wins, then a manual grant, otherwise the Free allowance
 * bundled with the paid plan. Display-only - the API decides what actually gets billed.
 * @param {object} orgData
 * @returns {object} An EXPENSES_TIERS entry, or EXPENSES_FREE_TIER
 */
export const getExpensesTier = (orgData) => getAddonTier(orgData, BILLING_PRODUCT.EXPENSES)

export const isExpensesFreeTier = (orgData) => isAddonFreeTier(orgData, BILLING_PRODUCT.EXPENSES)

/**
 * The Free allowance of AI-scanned receipts this org gets bundled with its paid plan -
 * smaller for AppSumo orgs. Use this rather than EXPENSES_FREE_TIER.includedQuota
 * anywhere the number is quoted at a specific org.
 * @param {object} orgData
 * @returns {number}
 */
export const getExpensesFreeQuota = (orgData) => getAddonFreeQuota(orgData, BILLING_PRODUCT.EXPENSES)

/**
 * Whether the org may have receipts read by AI at all. An active paid licence (or
 * Enterprise) is the floor whatever tier it holds, so buying AI Expenses on its own can't
 * work around an unpaid per-seat licence. Mirrors
 * orgMiddleware.requiresActiveExpensesPlan in mission-x-api.
 *
 * Deliberately not a quota check. How many scans an org has left this month is only
 * known server-side (the count comes off the expense collection, not the org document),
 * and a team member can't read the org's tier anyway - so the upload card is offered on
 * the licence alone and a spent month surfaces as a refusal on submit, with manual entry
 * one click away. Below this floor expenses still work; they're just typed in by hand,
 * which is the flow that has always existed.
 *
 * @param {object} orgData
 * @returns {boolean}
 */
export const canUseAiExpenses = (orgData) => Boolean(orgData?.isLicensedActive) || isOrgEnterprise(orgData)
